/**
 * CieloEngine implementation.
 */
const CieloEngine = () => {
  let tokenizationEndpoint = '';
  let threeDSTokenEndpoint = '';
  let sopAccessTokenEndpoint = '';

  /**
   * Initializes the CieloEngine with the provided config.
   * @param {object} config - Configuration object
   * @param {string} config.tokenizeEndpoint - The endpoint for backend tokenization (legacy)
   * @param {string} config.threeDSTokenEndpoint - The endpoint for 3DS token
   * @param {string} config.sopAccessTokenEndpoint - The endpoint for SOP access token
   */
  const init = ({
    tokenizeEndpoint,
    threeDSTokenEndpoint: threeDSTokenEndpointFromConfig,
    sopAccessTokenEndpoint: sopAccessTokenEndpointFromConfig,
  }) => {
    tokenizationEndpoint = tokenizeEndpoint;
    threeDSTokenEndpoint = threeDSTokenEndpointFromConfig;
    sopAccessTokenEndpoint = sopAccessTokenEndpointFromConfig || '';
  };

  /**
   * Appends the affiliation hints (purchase_token / company_code) to a token endpoint so the
   * backend can resolve the correct Cielo affiliation (empresa) for 3DS / SOP. Without them the
   * backend falls back to the tenant's default credentials, which breaks authentication for any
   * tenant that has more than one Cielo merchant: the challenge is signed with the default
   * merchant while the charge uses the resolved affiliation's merchant.
   * @param {string} endpoint - Base token endpoint.
   * @param {object} [params] - Affiliation hints.
   * @param {string} [params.purchaseToken] - Purchase token; backend resolves the assigned empresa.
   * @param {string} [params.companyCode] - Company/affiliation code; pre-payment fallback.
   * @returns {string} The endpoint with the affiliation query params appended.
   */
  const withAffiliationParams = (endpoint, { purchaseToken, companyCode } = {}) => {
    const query = new URLSearchParams();
    if (purchaseToken) query.set('purchase_token', purchaseToken);
    if (companyCode) query.set('company_code', companyCode);

    const queryString = query.toString();
    if (!queryString) return endpoint;

    const separator = endpoint.includes('?') ? '&' : '?';
    return `${endpoint}${separator}${queryString}`;
  };

  /**
   * Gets the 3DS authentication token from Cielo.
   * @param {object} [params] - purchaseToken / companyCode used to resolve the empresa.
   * @returns {Promise<string>} The 3DS authentication token
   * @throws {Error} If the request fails
   */
  const get3DSToken = async (params = {}) => {
    try {
      const url = withAffiliationParams(threeDSTokenEndpoint, params);
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to get 3DS token');
      }

      const data = await response.json();
      return data.access_token;
    } catch (error) {
      console.error('Error getting 3DS token:', error);
      throw error;
    }
  };

  /**
   * Gets the Silent Order Post (SOP) access token from backend.
   * @param {object} [params] - purchaseToken / companyCode used to resolve the empresa.
   * @returns {Promise<string>} The SOP access token
   * @throws {Error} If the request fails
   */
  const getSOPAccessToken = async (params = {}) => {
    if (!sopAccessTokenEndpoint) {
      throw new Error('SOP access token endpoint not configured');
    }

    const url = withAffiliationParams(sopAccessTokenEndpoint, params);
    const response = await fetch(url, {
      credentials: 'include', // Send cookies with the request
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get SOP access token: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    return data.access_token;
  };

  /**
   * Tokenizes a card using backend tokenization (legacy flow).
   * @param {Object} param - The card details.
   * @param {string} param.holderName - The card holder's name.
   * @param {string} param.cardNumber - The card number.
   * @param {string} param.expirationMonth - The card's expiration month.
   * @param {string} param.expirationYear - The card's expiration year.
   * @param {string} param.cardBrand - The card's brand.
   * @param {string} param.cvv - The card's CVV.
   * @returns {Promise<string>} The Cielo token.
   */
  const tokenizeCard = async ({
    holderName,
    cardNumber,
    expirationMonth,
    expirationYear,
    cardBrand,
    cvv,
  }) => {
    if (!tokenizationEndpoint) {
      throw new Error('Tokenization endpoint not configured');
    }

    const response = await fetch(tokenizationEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        card_holder_name: holderName,
        card_number: cardNumber,
        card_exp_month: expirationMonth,
        card_exp_year: expirationYear,
        card_brand: cardBrand,
        payment_engine: 'cielo',
        card_cvv: cvv,
      }),
    });

    const data = await response.json();

    if (!response.ok || data.error_code || !data.cielo_token) {
      const errorMessage = data.error_message || data.message || 'Tokenization failed';
      throw new Error(errorMessage);
    }

    return data.cielo_token;
  };

  return {
    init,
    tokenizeCard,
    get3DSToken,
    getSOPAccessToken,
  };
};

const cieloEngine = CieloEngine();

export default cieloEngine;
